import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * Soulmatch theme:
 *   - "light"  → Alvorada Mediterrânica (cream + gold + terracotta) — default
 *   - "dark"   → original dark navy + gold theme (for users who prefer high
 *                contrast / OLED-friendly UI)
 *
 * Implementation: toggles `alvorada` class on <body>. CSS overrides in
 * index.css under `body.alvorada` do all the colour remapping.
 */
const STORAGE_KEY = "soulmatch_theme";
const ThemeContext = createContext({ theme: "light", setTheme: () => {}, toggle: () => {} });

const applyToDom = (theme) => {
  if (theme === "light") document.body.classList.add("alvorada");
  else document.body.classList.remove("alvorada");
  // Toaster theme attribute (sonner reads it once but visual matches immediately)
  document.documentElement.dataset.theme = theme;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "dark" || saved === "light" ? saved : "light";
    } catch { return "light"; }
  });

  useEffect(() => { applyToDom(theme); }, [theme]);

  const setTheme = useCallback((t) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch (err) { console.warn("[theme persist]", err); }
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  // Memoized so context consumers don't re-render unless theme actually
  // changes (setters are stable thanks to useCallback above).
  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
